import angular from 'angular';
import moment from 'moment';
import {
    formatBytes,
    limparInputArquivo,
    montarMeses,
    montarSelectCompetencias,
    TAMANHO_MAXIMO_ARQUIVO,
    validarArquivo,
} from '@utils/upload-arquivo';
import { ITipoArquivoResponse } from '../../../../../TipoArquivos/models/tipo-arquivos.service.model';
import { TipoArquivosService } from '../../../../../TipoArquivos/tipo-arquivos.service';
import { IUploadDocumentoForm } from '../../models/upload-documentos-em-lote.form.model';

/**
 * Campos do documento, os mesmos do upload individual, mais o tipo de documento —
 * implícito no fluxo individual (vem da pasta aberta) e escolhido aqui.
 *
 * As regras de PDF, tamanho e competência vêm de `@utils/upload-arquivo`, o mesmo
 * helper que o modal individual consome.
 */
export class DadosDoDocumentoFormController {

    static $inject = [
        '$scope',
        '$rootScope',
        'TipoArquivosService',
        'NewToaster',
    ];

    public form: angular.IFormController;
    public editable = false;
    public entity: IUploadDocumentoForm;

    public readonly nomeMaxLength = 100;
    public readonly tamanhoMaximoArquivo = TAMANHO_MAXIMO_ARQUIVO;
    public readonly tamanhoMaximoArquivoLabel = formatBytes(TAMANHO_MAXIMO_ARQUIVO, 0);
    public readonly inputArquivoId = 'arquivoUploadLote';

    public tipos: ITipoArquivoResponse[] = [];
    public isArquivoValido = false;
    public selectCompetencias = montarSelectCompetencias(moment, undefined);
    public busy = false;

    constructor(
        public $scope: angular.IScope,
        public $rootScope: angular.IRootScopeService & {
            configuracoes: { [key: string]: unknown },
        },
        public TipoArquivosService: TipoArquivosService,
        public NewToaster: { pop: (opts: { type: string; title: string; body?: string }) => void },
    ) { }

    $onInit() {
        this.initializeEntity();
        this.selectCompetencias = montarSelectCompetencias(moment, this.getDataFechamentoFolha());
        this.carregaTipos();
        this.observeTipoArquivo();
    }

    initializeEntity() {
        this.entity = this.entity || {} as IUploadDocumentoForm;
        this.entity.tipoarquivo_codigo = this.entity.tipoarquivo_codigo || '';
        this.entity.nome = this.entity.nome || '';
        this.entity.conteudo = this.entity.conteudo || '';
        this.entity.competenciaano = this.entity.competenciaano ?? null;
        this.entity.competenciames = this.entity.competenciames ?? null;
        this.entity.arquivo = this.entity.arquivo || null;
        this.entity.tipoarquivo_selecionado_ui = this.entity.tipoarquivo_selecionado_ui || null;
    }

    // ---------- tipo de documento ----------

    /**
     * Só tipos que aceitam upload: pastas padrão do sistema são alimentadas pelo
     * próprio sistema — mesma regra de canUploadArquivo() na aba do colaborador.
     */
    carregaTipos() {
        this.busy = true;
        this.TipoArquivosService.getList()
            .then((result) => {
                this.tipos = (result || []).filter((tipo) => !tipo.tipopadrao);
            })
            .catch(() => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Não foi possível carregar os tipos de documento.',
                });
            })
            .finally(() => this.busy = false);
    }

    observeTipoArquivo() {
        this.$scope.$watch(
            () => this.entity?.tipoarquivo_selecionado_ui,
            () => {
                this.entity.tipoarquivo_codigo = this.entity.tipoarquivo_selecionado_ui?.codigo || '';
            },
            true,
        );
    }

    // ---------- nome ----------

    validateNomeField() {
        if (!this.form?.nome?.$setValidity) {
            return;
        }

        const nome = this.normalizeNome();
        this.form.nome.$setValidity('trimmedRequired', !!nome);
        this.form.nome.$setValidity('maxLength', nome.length <= this.nomeMaxLength);
    }

    normalizeNome() {
        return (this.entity.nome || '').trim();
    }

    // ---------- competência ----------

    getDataFechamentoFolha() {
        return this.$rootScope.configuracoes?.DATA_FECHAMENTO_FOLHA;
    }

    setSelectMonths(year?: number | null) {
        this.entity.competenciames = null;
        this.selectCompetencias.meses = year
            ? montarMeses(moment, this.getDataFechamentoFolha(), year)
            : [];
    }

    // ---------- arquivo ----------

    onFileChange() {
        this.isArquivoValido = this.validarArquivoSelecionado();
        this.$scope.$applyAsync();
    }

    formatBytes(bytes: number, decimals: number = 2) {
        return formatBytes(bytes, decimals);
    }

    getArquivoSelecionado(): File | null {
        return this.entity.arquivo?.[0] || null;
    }

    validarArquivoSelecionado() {
        const resultado = validarArquivo(this.getArquivoSelecionado());

        if (resultado.valido) {
            return true;
        }

        if (resultado.erro && resultado.erro.tipo !== 'ausente') {
            this.limparArquivo();
            this.NewToaster.pop({
                type: resultado.erro.toasterType,
                title: resultado.erro.title,
                body: resultado.erro.body,
            });
        }

        return false;
    }

    limparArquivo() {
        this.entity.arquivo = null;
        this.entity.conteudo = '';
        this.isArquivoValido = false;
        limparInputArquivo(this.inputArquivoId);
    }
}
